/**
 * 메일 즐겨찾기 폴더 목록(settings.email.favoriteMailboxes) 계산 헬퍼.
 *
 * 데이터 모델: 순서가 곧 배열 순서인 즐겨찾기 항목(entry) 목록(string[]). 웹(JMAP)·데스크탑(IMAP) 공용.
 *   - undefined : 사용자가 아직 커스터마이즈하지 않음 → role 기반 기본값을 계산해 보여주되 서버에 저장하지 않는다.
 *   - []        : 사용자가 전부 제거함 → 빈 목록을 존중한다.
 *
 * 항목 형식(canonical):
 *   - 실 메일함  : UTF-8 디코딩된 raw 이름(로케일 표시명 아님)을 '/' 로 이은 전체 경로.
 *                  inbox role 메일함은 'INBOX', 그 하위는 'INBOX/<name>/...', 그 외는 최상위부터의 경로.
 *                  (libs/jamUtil/openSearchMap 의 toImapPath 와 동일 규칙)
 *   - 가상 메일함 : 'virtual:<id>' (예: 'virtual:unread', 'virtual:flagged')
 *
 * 순수 함수만 둔다(UI·DataManager 의존 없음). 메일함 → 경로 변환과 기본값은 호출자가 계산해 넘긴다.
 */

export const VIRTUAL_FAVORITE_PREFIX = 'virtual:'

/** 즐겨찾기 항목 ↔ 메일함 id 변환에 쓰는 현재 메일함 기준 맵. */
export interface FavoriteEntryMaps {
  /** 실 메일함 canonical 경로 → mailbox id */
  pathToId: Record<string, string>
  /** 실 메일함 mailbox id → canonical 경로 */
  idToPath: Record<string, string>
  /** 가상(비실) 메일함 id 목록 (unread, flagged, schedule, spamsniper 등) */
  virtualIds: string[]
}

export interface FavoriteMailboxLike {
  getId: () => string
  isRealMailbox?: boolean
}

export const toVirtualFavoriteEntry = (mailboxId: string): string =>
  `${VIRTUAL_FAVORITE_PREFIX}${mailboxId}`

const isVirtualFavoriteEntry = (entry: string): boolean => entry.startsWith(VIRTUAL_FAVORITE_PREFIX)

/**
 * 메일함 목록으로 변환 맵을 만든다. 실 메일함(isRealMailbox === true)만 경로를 가지며,
 * 경로가 빈 문자열(이름 없음)이면 제외한다. 비실 메일함(schedule/spamsniper 등)은 경로로 취급하지 않는다.
 */
export const buildFavoriteEntryMaps = <T extends FavoriteMailboxLike>(
  mailboxes: T[],
  toPath: (mailbox: T) => string,
): FavoriteEntryMaps => {
  const maps: FavoriteEntryMaps = { pathToId: {}, idToPath: {}, virtualIds: [] }
  mailboxes.forEach((mailbox) => {
    const mailboxId = mailbox.getId()
    if (mailbox.isRealMailbox !== true) {
      if (!maps.virtualIds.includes(mailboxId)) maps.virtualIds.push(mailboxId)
      return
    }
    const path = toPath(mailbox)
    if (path === '') return
    maps.pathToId[path] = mailboxId
    maps.idToPath[mailboxId] = path
  })
  return maps
}

/** mailbox id → 즐겨찾기 항목. 맵에 없는(경로 없는 실 메일함 포함) id 는 null. */
export const toFavoriteEntry = (mailboxId: string, maps: FavoriteEntryMaps): string | null => {
  if (maps.virtualIds.includes(mailboxId)) return toVirtualFavoriteEntry(mailboxId)
  return maps.idToPath[mailboxId] ?? null
}

/** 즐겨찾기 항목 → mailbox id. 현재 메일함으로 해석할 수 없으면 null(표시하지 않음). */
export const resolveFavoriteEntryToId = (entry: string, maps: FavoriteEntryMaps): string | null => {
  if (isVirtualFavoriteEntry(entry)) {
    const mailboxId = entry.slice(VIRTUAL_FAVORITE_PREFIX.length)
    return maps.virtualIds.includes(mailboxId) ? mailboxId : null
  }
  return maps.pathToId[entry] ?? null
}

const dedupe = (entries: string[]): string[] => Array.from(new Set(entries))

/**
 * 저장값을 canonical 형식으로 정규화한다(멱등).
 *   - 'virtual:<x>' / 현재 경로로 해석되는 항목 → 유지
 *   - 실 메일함 id 와 같은 항목(구 형식) → 경로로 교체
 *   - 가상 메일함 id 와 같은 항목(구 형식 'unread' 등) → 'virtual:<id>'
 *   - 그 외(해석 불가) → 그대로 유지. 폴더가 다시 생기거나 메일함 목록이 아직 일부만 로드됐을 수 있다.
 * 중복은 첫 항목만 남긴다. 교체·중복 제거가 하나라도 있으면 changed = true.
 */
export const normalizeFavoriteEntries = (
  stored: string[],
  maps: FavoriteEntryMaps,
): { entries: string[]; changed: boolean } => {
  const converted = stored.map((entry) => {
    if (isVirtualFavoriteEntry(entry) || maps.pathToId[entry] != null) return entry
    if (maps.idToPath[entry] != null) return maps.idToPath[entry]
    if (maps.virtualIds.includes(entry)) return toVirtualFavoriteEntry(entry)
    return entry
  })
  const entries = dedupe(converted)
  const changed =
    entries.length !== stored.length || entries.some((entry, index) => entry !== stored[index])
  return { entries, changed }
}

/**
 * 구버전 로컬(IndexedDB) id 목록을 즐겨찾기 항목으로 변환한다(마이그레이션 전용).
 * 현재 메일함으로 해석되지 않는 id(이미 삭제된 폴더)는 버린다.
 */
export const convertLegacyIdsToEntries = (ids: string[], maps: FavoriteEntryMaps): string[] =>
  dedupe(
    ids
      .map((mailboxId) => toFavoriteEntry(mailboxId, maps))
      .filter((entry): entry is string => entry != null),
  )

/**
 * 메일함 이름 변경·이동으로 경로가 oldPath → newPath 로 바뀌었을 때 항목을 다시 쓴다.
 * oldPath 자신과 하위 경로(oldPath + '/...')를 모두 바꾼다. 결과 중복은 첫 항목만 남긴다.
 */
export const renameFavoriteEntries = (
  entries: string[],
  oldPath: string,
  newPath: string,
): { entries: string[]; changed: boolean } => {
  if (oldPath === '' || newPath === '' || oldPath === newPath) {
    return { entries: [...entries], changed: false }
  }
  const prefix = `${oldPath}/`
  let changed = false
  const renamed = entries.map((entry) => {
    if (entry === oldPath) {
      changed = true
      return newPath
    }
    if (entry.startsWith(prefix)) {
      changed = true
      return `${newPath}/${entry.slice(prefix.length)}`
    }
    return entry
  })
  const deduped = dedupe(renamed)
  return { entries: deduped, changed: changed || deduped.length !== renamed.length }
}

/** 저장된 값(서버/Redux)과 기본값으로 현재 유효한 즐겨찾기 항목 목록을 계산한다. */
export const resolveFavoriteEntries = (
  stored: string[] | undefined | null,
  defaultEntries: string[],
): string[] => {
  if (!Array.isArray(stored)) return [...defaultEntries]
  return [...stored]
}

/** 유효 목록 끝에 항목을 추가한 새 배열을 반환한다. 이미 있으면 그대로 복사본을 반환한다. */
export const addFavoriteEntry = (current: string[], entry: string): string[] => {
  if (current.includes(entry)) return [...current]
  return [...current, entry]
}

/** 유효 목록에서 항목을 제거한 새 배열을 반환한다. */
export const removeFavoriteEntry = (current: string[], entry: string): string[] =>
  current.filter((item) => item !== entry)

interface LegacyFavoriteItem {
  id: string
  order: number
}

const isLegacyFavoriteItem = (item: unknown): item is LegacyFavoriteItem => {
  if (item == null || typeof item !== 'object') return false
  const record = item as Record<string, unknown>
  return typeof record.id === 'string' && typeof record.order === 'number'
}

/**
 * 구버전 로컬(IndexedDB `<user>-favoriteMailboxList`) 데이터
 * `{ isInitialized, idList: [{ id, order }], version }` 에서 order 순으로 정렬한 id 배열을 뽑는다.
 * isInitialized !== true 이거나 형식이 맞지 않으면 null(이전할 사용자 데이터 없음)을 반환한다.
 */
export const pickLegacyFavoriteMailboxIds = (legacy: unknown): string[] | null => {
  if (legacy == null || typeof legacy !== 'object') return null
  const record = legacy as Record<string, unknown>
  if (record.isInitialized !== true || !Array.isArray(record.idList)) return null

  const ids = record.idList
    .filter(isLegacyFavoriteItem)
    .sort((a, b) => a.order - b.order)
    .map((item) => item.id)
  return Array.from(new Set(ids))
}

/** 두 항목 목록이 같은 집합인지(순서·중복 무관) 비교한다. legacy 값이 role 기본값 그대로인지 판별하는 데 쓴다. */
export const isSameFavoriteSet = (a: string[], b: string[]): boolean => {
  const setA = new Set(a)
  const setB = new Set(b)
  if (setA.size !== setB.size) return false
  for (const id of setA) {
    if (!setB.has(id)) return false
  }
  return true
}

/**
 * 서버에서 받은 email 카테고리에 favoriteMailboxes 가 빠졌을 때 웹 현재값을 보존할지 판정한다.
 * 웹은 이 필드를 undefined 로 만들지 않으므로(전부 제거 = []), "현재값 정의됨 + 서버값 undefined" 는
 * 허용 키 필터로 필드를 버리는 구버전 클라이언트가 full-replace PUT 으로 지운 것이다
 * → 현재값을 유지하고 서버에 다시 올려야 한다. 현재값도 undefined 면 아무것도 하지 않는다.
 */
export const preserveFavoriteMailboxes = (
  serverEmail: unknown,
  currentEmail: unknown,
): { email: Record<string, unknown>; needsReupload: boolean } => {
  const server =
    serverEmail != null && typeof serverEmail === 'object'
      ? (serverEmail as Record<string, unknown>)
      : {}
  const current =
    currentEmail != null && typeof currentEmail === 'object'
      ? (currentEmail as Record<string, unknown>)
      : {}

  if (server.favoriteMailboxes !== undefined || !Array.isArray(current.favoriteMailboxes)) {
    return { email: server, needsReupload: false }
  }
  return {
    email: { ...server, favoriteMailboxes: [...current.favoriteMailboxes] },
    needsReupload: true,
  }
}
